import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, Search, TrendingUp, Check, X, Loader, ChevronRight, ChevronLeft, ChevronsDown, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { handleError, getUserFriendlyErrorMessage } from '../utils/errorUtils';

interface KeywordAnalysisReadOnlyProps {
  domain: string;
  prospectId?: string;
}

type OpportunityLevel = 'Forte' | 'Moyenne' | 'Faible' | 'Moyenne / Faible';

interface KeywordWithOpportunity {
  id: number;
  keyword: string;
  position: number;
  search_volume: number;
  displayPosition: JSX.Element;
  opportunity: OpportunityLevel;
  opportunityColor: string;
  opportunityDetail: string;
  isBranded: boolean;
}

/**
 * KeywordAnalysisReadOnly - Affiche les mots-clés existants pour un domaine (lecture seule)
 * Version spéciale pour la page de démonstration qui n'affiche que les données existantes
 * sans possibilité de rafraîchissement pour éviter de consommer le budget
 */
interface KeywordData {
  keyword: string;
  position: number;
  search_volume: number;
  url?: string;
}

const KeywordAnalysisReadOnly: React.FC<KeywordAnalysisReadOnlyProps> = ({ domain, prospectId }) => {
  const [keywords, setKeywords] = useState<KeywordData[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Commencer avec loading=true pour vérifier les données existantes
  const [error, setError] = useState<string | null>(null);
  const [dataExists, setDataExists] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>('keyword');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Mémoriser la fonction d'obtention d'icône pour éviter de la recréer à chaque rendu
  const getPositionIcon = useCallback((position: number) => {
    if (position <= 10) {
      return <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">Top 10</span>;
    } else if (position <= 30) {
      return <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">Top 30</span>;
    } else {
      return <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">{position}</span>;
    }
  }, []);

  // Mémoriser la fonction de génération de mots-clés fictifs pour éviter de la recréer à chaque rendu
  const generateMockKeywords = useCallback((domainName: string): KeywordData[] => {
    const cleanDomain = domainName.replace(/^www\./, '').replace(/\.(com|ch|fr|net|org).*$/, '');
    
    return [
      {
        keyword: cleanDomain,
        position: 1,
        search_volume: 1000
      },
      {
        keyword: `${cleanDomain} contact`,
        position: 5,
        search_volume: 300
      },
      {
        keyword: `${cleanDomain} services`,
        position: 12,
        search_volume: 200
      },
      {
        keyword: `${cleanDomain} avis`,
        position: 18,
        search_volume: 150
      },
      {
        keyword: `${cleanDomain} prix`,
        position: 25,
        search_volume: 100
      }
    ];
  }, []);
  
  // Vérifier automatiquement s'il existe des données pour ce prospect
  useEffect(() => {
    const checkExistingData = async () => {
      if (!domain && !prospectId) {
        setLoading(false);
        return;
      }

      try {
        // Si nous avons un ID de prospect, vérifier s'il y a des données existantes
        if (prospectId) {
          const { data: storedData, error } = await supabase
            .from('keyword_analysis_results')
            .select('*')
            .eq('prospect_id', prospectId)
            .maybeSingle();
            
          if (error) {
            const appError = handleError(error, { component: 'KeywordAnalysisReadOnly', action: 'checkExistingData', prospectId });
            console.error('Erreur lors de la vérification des données existantes:', appError);
            setError(getUserFriendlyErrorMessage(appError));
          } else if (storedData && storedData.ranked_keywords && storedData.ranked_keywords.length > 0) {
            // Des données existent, les formater et les afficher
            const formattedKeywords = storedData.ranked_keywords.map((kw: { keyword: string; rank_group: number; search_volume: number; url?: string }) => ({
              keyword: kw.keyword,
              position: kw.rank_group,
              search_volume: kw.search_volume,
              url: kw.url
            }));
            setKeywords(formattedKeywords);
            setDataExists(true);
          } else {
            // Aucune donnée existante
            setDataExists(false);
            setKeywords([]);
          }
        } else if (domain) {
          // Pas d'ID de prospect mais un domaine, utiliser des mots-clés fictifs
          setKeywords(generateMockKeywords(domain));
          setDataExists(true); // Considérer que nous avons des données pour l'affichage
        }
      } catch (err) {
        const appError = handleError(err, { component: 'KeywordAnalysisReadOnly', action: 'checkExistingData', domain });
        console.error('Exception lors de la vérification des données existantes:', appError);
        setError(getUserFriendlyErrorMessage(appError));
      } finally {
        setLoading(false);
      }
    };

    checkExistingData();
  }, [domain, prospectId, generateMockKeywords]);

  // Déterminer si un mot-clé est probablement une marque
  const isBrandedKeyword = useCallback((keyword: string, domain: string): boolean => {
    // Simplifier le domaine pour la comparaison (enlever www., .com, etc.)
    const simpleDomain = domain.replace(/^www\./, '').replace(/\.(com|ch|fr|net|org).*$/, '');
    
    // Vérifier si le mot-clé contient le nom du domaine
    return keyword.toLowerCase().includes(simpleDomain.toLowerCase());
  }, []);
  
  // Calculer l'opportunité d'un mot-clé selon les critères définis
  const calculateOpportunity = useCallback((keyword: string, volume: number, position: number, domainName: string): { 
    level: OpportunityLevel, 
    color: string,
    detail: string,
    isBranded: boolean 
  } => {
    // Déterminer si c'est un mot-clé de marque
    const branded = isBrandedKeyword(keyword, domainName);
    
    // Mot-clé de marque, déjà en Top 10
    if (branded && position <= 10) {
      return { 
        level: 'Faible', 
        color: '#d63031',
        detail: 'Faible — déjà acquis',
        isBranded: true
      };
    }
    
    // Mot-clé de marque pour la navigation
    if (branded && keyword.includes('contact')) {
      return { 
        level: 'Faible', 
        color: '#d63031',
        detail: 'Faible — navigation',
        isBranded: true
      };
    }
    
    // Mot-clé non-marque avec volume > 200 et position entre 15-40
    if (!branded && volume > 200 && position > 15 && position < 40) {
      return { 
        level: 'Forte', 
        color: '#00b894',
        detail: 'Forte — potentiel élevé',
        isBranded: false 
      };
    }
    
    // Mot-clé non-marque avec volume entre 100-200 et position entre 5-15
    if (!branded && volume >= 100 && volume <= 200 && position >= 5 && position <= 15) {
      return { 
        level: 'Moyenne', 
        color: '#fdcb6e',
        detail: 'Moyenne (informative)',
        isBranded: false 
      };
    }
    
    // Mot-clé avec intention transactionnelle
    if (keyword.includes('prix') || keyword.includes('tarif') || keyword.includes('coût') || keyword.includes('acheter')) {
      return { 
        level: 'Moyenne', 
        color: '#fdcb6e',
        detail: 'Moyenne (intent B2C)',
        isBranded: false 
      };
    }
    
    // Mot-clé informationnel
    if (keyword.includes('avis') || keyword.includes('review')) {
      return { 
        level: 'Moyenne / Faible', 
        color: '#fdcb6e',
        detail: 'Moyenne / Faible',
        isBranded: false 
      };
    }
    
    // Mot-clé spécifique avec faible volume
    if (volume < 50) {
      return { 
        level: 'Faible', 
        color: '#d63031',
        detail: 'Faible — volume limité',
        isBranded: false 
      };
    }
    
    // Cas par défaut - analyse standard
    if (volume > 200 && position > 10) {
      return { 
        level: 'Moyenne', 
        color: '#fdcb6e',
        detail: 'Moyenne — à évaluer',
        isBranded: false 
      };
    } else {
      return { 
        level: 'Faible', 
        color: '#d63031',
        detail: 'Faible — priorité basse',
        isBranded: false 
      };
    }
  }, [isBrandedKeyword]);

  // Mémoriser les mots-clés formatés pour éviter de les recalculer inutilement
  const formattedKeywords: KeywordWithOpportunity[] = useMemo(() => {
    if (!keywords) return [];
    
    return keywords.map((keyword, index) => {
      const { level, color, detail, isBranded } = calculateOpportunity(keyword.keyword, keyword.search_volume, keyword.position, domain || '');
      return {
        ...keyword,
        id: index,
        displayPosition: getPositionIcon(keyword.position),
        opportunity: level,
        opportunityColor: color,
        opportunityDetail: detail,
        isBranded: isBranded
      };
    });
  }, [keywords, getPositionIcon, calculateOpportunity, domain]);
  
  // Fonction de tri des mots-clés
  const handleSort = useCallback((field: string) => {
    if (sortField === field) {
      // Même champ, inverser la direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouveau champ, définir asc par défaut
      setSortField(field);
      setSortDirection('asc');
    }
    // Revenir à la première page après un tri
    setCurrentPage(1);
  }, [sortField, sortDirection]);
  
  // Appliquer le tri aux mots-clés
  const sortedKeywords = useMemo(() => {
    if (!formattedKeywords.length) return [];
    
    return [...formattedKeywords].sort((a, b) => {
      let valueA, valueB;
      
      // Déterminer les valeurs à comparer selon le champ de tri
      switch (sortField) {
        case 'keyword':
          valueA = a.keyword.toLowerCase();
          valueB = b.keyword.toLowerCase();
          break;
        case 'volume':
          valueA = a.search_volume || 0;
          valueB = b.search_volume || 0;
          break;
        case 'position':
          valueA = a.position;
          valueB = b.position;
          break;
        case 'branded':
          valueA = a.isBranded ? 1 : 0;
          valueB = b.isBranded ? 1 : 0;
          break;
        case 'opportunity': {
          // Tri par niveau d'opportunité (Forte > Moyenne > Faible)
          const opportunityRank = {
            'Forte': 3,
            'Moyenne': 2,
            'Moyenne / Faible': 1,
            'Faible': 0
          };
          valueA = opportunityRank[a.opportunity as keyof typeof opportunityRank] || 0;
          valueB = opportunityRank[b.opportunity as keyof typeof opportunityRank] || 0;
          break;
        }
        default:
          valueA = a.keyword.toLowerCase();
          valueB = b.keyword.toLowerCase();
      }
      
      // Appliquer le tri dans la bonne direction
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [formattedKeywords, sortField, sortDirection]);

  // Afficher un indicateur de chargement pendant la vérification des données
  if (loading) {
    return (
      <div className="space-y-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <Loader className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500">
            Chargement des données SEO...
          </p>
        </div>
      </div>
    );
  }
  
  // Si aucune donnée n'existe et qu'il n'y a pas d'erreur, afficher un message
  if (!dataExists && !error && !keywords?.length) {
    return (
      <div className="space-y-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <Search className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analyse des mots-clés SEO</h3>
          <p className="text-sm text-gray-500 mb-6">
            Aucune analyse SEO n'a encore été effectuée pour ce domaine. Veuillez contacter l'administrateur pour lancer une analyse.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-component-name="KeywordAnalysisReadOnly">
      <div className="p-5">
        {error && (
          <div className="bg-red-50 p-4 rounded-md mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center">
            <div className="animate-pulse flex space-x-4 justify-center">
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
            </div>
            <p className="text-gray-500 mt-4">Chargement des mots-clés...</p>
          </div>
        ) : (
          <>
            {keywords && keywords.length > 0 ? (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{keywords.length}</span> mots-clés trouvés pour <span className="font-mono text-indigo-600">{domain}</span>
                  </p>
                </div>
                
                <div className="overflow-x-auto border rounded-lg">
                  <div className="bg-gray-100 p-2 flex justify-between items-center">
                    <div className="text-xs text-gray-600">
                      Page {currentPage} / {sortedKeywords.length > 0 ? Math.ceil(sortedKeywords.length / 10) : 1}
                    </div>
                    <button 
                      onClick={() => setShowAll(!showAll)} 
                      className="flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      {showAll ? 'Réduire' : 'Tout développer'}
                      <ChevronsDown className="h-3 w-3 ml-1" />
                    </button>
                  </div>
                  
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          scope="col" 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('keyword')}
                        >
                          <div className="flex items-center">
                            Mot-clé
                            {sortField === 'keyword' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('volume')}
                        >
                          <div className="flex items-center">
                            Volume
                            {sortField === 'volume' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('position')}
                        >
                          <div className="flex items-center">
                            Position
                            {sortField === 'position' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('branded')}
                        >
                          <div className="flex items-center">
                            Marque ?
                            {sortField === 'branded' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('opportunity')}
                        >
                          <div className="flex items-center">
                            Opportunité
                            {sortField === 'opportunity' && (
                              <span className="ml-1">
                                {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              </span>
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(showAll ? sortedKeywords : sortedKeywords.slice((currentPage - 1) * 10, currentPage * 10)).map((keyword) => (
                        <tr key={keyword.id} className={keyword.id % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900">
                            {keyword.keyword}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500">
                            {keyword.search_volume ? keyword.search_volume.toLocaleString() : 'N/A'}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              {getPositionIcon(keyword.position)}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-sm">
                            <div className="flex items-center justify-center">
                              {keyword.isBranded ? (
                                <div className="bg-green-100 p-1 rounded-full">
                                  <Check className="h-4 w-4 text-green-600" />
                                </div>
                              ) : (
                                <div className="bg-red-100 p-1 rounded-full">
                                  <X className="h-4 w-4 text-red-600" />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              <span 
                                className="px-2 py-0.5 text-xs font-medium rounded" 
                                style={{ backgroundColor: `${keyword.opportunityColor}20`, color: keyword.opportunityColor, fontWeight: 'bold' }}
                              >
                                {keyword.opportunityDetail}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination controls */}
                {!showAll && sortedKeywords.length > 10 && (
                  <div className="flex justify-between items-center mt-4 px-2">
                    <button 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`flex items-center text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-800'}`}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Précédent
                    </button>
                    <div className="text-sm text-gray-500">
                      {(currentPage - 1) * 10 + 1} - {Math.min(currentPage * 10, sortedKeywords.length)} sur {sortedKeywords.length}
                    </div>
                    <button 
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(sortedKeywords.length / 10), p + 1))}
                      disabled={currentPage >= Math.ceil(sortedKeywords.length / 10)}
                      className={`flex items-center text-sm ${currentPage >= Math.ceil(sortedKeywords.length / 10) ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-800'}`}
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                )}

                {/* Section d'analyse d'impact */}
                <div className="mt-6 p-4 border border-red-100 bg-red-50 rounded-lg">
                  <div className="flex items-start space-x-3 mb-1">
                    <div className="h-6 w-6 bg-red-200 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-base text-red-800 font-bold">Recommandations:</h3>
                    </div>
                  </div>
                  <div className="ml-9">
                    {formattedKeywords.length < 10 ? (
                      <>
                        <p className="text-sm text-red-800 mt-2">
                          Le nombre limité de mots-clés détectés ({formattedKeywords.length}) indique un potentiel inexploité. Une stratégie SEO ciblée pourrait multiplier votre visibilité par 3 et attirer un public hautement qualifié vers votre site.
                        </p>
                        <p className="text-sm text-red-800 mt-2">
                          En travaillant 3 à 5 mots-clés non-brand, vous pourriez capter jusqu'à {Math.round(formattedKeywords.reduce((sum, kw) => sum + (kw.search_volume || 0), 0) * 0.5)} visiteurs supplémentaires par mois avec une intention réelle d'achat.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-red-800 mt-2">
                          Félicitations pour votre base solide de {formattedKeywords.length} mots-clés. Votre présence SEO montre un bon potentiel, mais pourrait être optimisée davantage pour maximiser vos conversions.
                        </p>
                        <p className="text-sm text-red-800 mt-2">
                          Une stratégie d'optimisation ciblée sur vos 5 meilleurs mots-clés non-brand pourrait augmenter votre trafic qualifié de 35% et améliorer significativement votre taux de conversion.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <Search className="h-8 w-8 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucun mot-clé trouvé pour ce domaine.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Utiliser memo pour les données de mots-clés formatées
const MemoizedKeywordAnalysisReadOnly = React.memo(KeywordAnalysisReadOnly);

export default MemoizedKeywordAnalysisReadOnly;
